<template>
    <div>
        <Hero title="Changelog" colour-index="0" :left="true" />

        <div class="mx-3 sm:mx-12 my-3 sm:my-12">
            <div v-for="(release, index) in _RELEASES" :key="release.title" class="lg:flex mb-4">
                <div
                    class="text-medium-gray lg:pr-12 font-bold xs:text-left lg:text-right lg:w-1/5 xs:w-full mb-6 lg:mb-0">
                    <span v-if="index === 0 || release.released_at !== _RELEASES[index - 1].released_at">{{
                        release.released_at.substring(0, 10) }}</span>
                </div>

                <div class="sx:w-full lg:w-4/5 border-l-8 px-4 pb-4 border-[var(--dark-gray)]">
                    <div class="text-light-gray mb-4 overflow-hidden">
                        <strong>
                            {{ release.title }}
                        </strong>

                        <Badge v-for="tag in release.tags.filter((t) => t !== 'passthrough')" :key="tag" :label="tag"
                            class="ml-4" />
                    </div>

                    <div class="timeline-desc text-primary-white" v-html="release.description"></div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { setTitle } from '~~/services/common';

setTitle('Changelog', 'Stay updated with our Unified API product changelog, featuring the latest features, enhancements, and fixes. ');
</script>

<script lang="ts">
import RELEASES from '~/data/releases.json';
import markdown from 'markdown-it';
import { INTEGRATIONS } from '~/services/integrations';
import { MAP_CATS } from '~~/services/categories';
import _CHANGELOG from '~/data/changelog.json';
import { type IIntegration, type TIntegrationCategory, type TIntegrationSupportWebhookType, type TObjectType, type TWebhookEvent } from '~/models/Unified';

export type TChangelogMailinglistCategory = {
    category: TIntegrationCategory;
    category_name?: string;
    items: string[];
};

type TChangelogPagination = {
    type: 'pagination';
    object_type: TObjectType;
    pagination: string;
};
type TChangelogFields = {
    type: 'readable_field' | 'writable_field';
    object_type: TObjectType;
    field: string;
};
type TChangelogObject = {
    type: `webhook-${TWebhookEvent}-${TIntegrationSupportWebhookType}` | 'object';
    object_type: TObjectType;
};
type TChangelogIntegration = {
    type: 'integration' | 'oauth2' | 'sign-in';
};
type TChangelogMethod = {
    type: 'method';
    object_type: TObjectType;
    method: string;
};

export type TChangelog = {
    created_at: string;
    integration_type: IIntegration['type'];
    integration_name: IIntegration['name'];
    categories: IIntegration['categories'];
} & (TChangelogIntegration | TChangelogObject | TChangelogFields | TChangelogPagination | TChangelogMethod);

// YYYY-MM-DD : changelog.type : integration.type : any
const CL_SUMMARY: { [path in string]: { [path in TChangelog['type']]: { [path in TChangelog['integration_type']]: any[] } } } = {};
_CHANGELOG
    .filter((c) => c.type !== 'integration')
    .forEach((c) => {
        const date = c.created_at.substring(0, 10);
        CL_SUMMARY[date] = CL_SUMMARY[date] || {};
        const cl_type = c.type as TChangelog['type'];
        CL_SUMMARY[date][cl_type] = CL_SUMMARY[date][cl_type] || {};
        const i_type = c.integration_type;
        CL_SUMMARY[date][cl_type][i_type] = CL_SUMMARY[date][cl_type][i_type] || [];
        CL_SUMMARY[date][cl_type][i_type].push(c);
    });

const Markdown = new markdown();
interface IRelease {
    description: string;
    released_at: string;
    tags: string[];
    title: string;
}

export default {
    data() {
        return {
            _RELEASES: RELEASES.map((r) => {
                return {
                    title: r.title,
                    description: Markdown.render(r.description || ''),
                    released_at: this.formatDate(r.released_at),
                    tags: r.tags,
                };
            })
                .concat(
                    INTEGRATIONS.filter((i) => i.created_at && !i.in_progress).map((i) => {
                        return {
                            title: i.name,
                            description: Markdown.render(
                                `New ${i.categories
                                    .filter((c) => MAP_CATS[c as keyof typeof MAP_CATS])
                                    .map((c) => MAP_CATS[c as keyof typeof MAP_CATS])
                                    .join('/')} integration: [${i.name}](/integrations/${i.type})`
                            ),
                            released_at: this.formatDate(i.created_at),
                            tags: i.categories.filter((c) => MAP_CATS[c as keyof typeof MAP_CATS]).map((c) => MAP_CATS[c as keyof typeof MAP_CATS] as string)//.concat([i.name] as any),
                        };
                    })
                )
                .concat(
                    Object.keys(CL_SUMMARY).map((date) => {
                        const release: IRelease = { released_at: date, title: 'Enhancements', tags: [], description: '<ul>' };

                        const cl_types = Object.keys(CL_SUMMARY[date] || {}) as TChangelog['type'][];
                        for (const cl_type of cl_types) {
                            const i_types = Object.keys(CL_SUMMARY[date][cl_type] || {});
                            for (const i_type of i_types) {
                                const cl = CL_SUMMARY[date][cl_type][i_type];
                                const desc: string[] = [];
                                const objects: string[] = this.unique(cl, 'object_type');
                                switch (cl_type) {
                                    case 'readable_field':
                                        objects.forEach((object) => {
                                            desc.push(
                                                `${this.integration_link(cl[0])} ${this.object_name(object)} added new <b>readable fields</b>: ${this.join(
                                                    cl.filter((c) => c.object_type === object).map((f) => f.field)
                                                )}`
                                            );
                                        });
                                        break;
                                    case 'writable_field':
                                        objects.forEach((object) => {
                                            desc.push(
                                                `${this.integration_link(cl[0])} ${this.object_name(object)} added new <b>writable fields</b>: ${this.join(
                                                    cl.filter((c) => c.object_type === object).map((f) => f.field)
                                                )}`
                                            );
                                        });
                                        break;
                                    case 'sign-in':
                                        desc.push(`${this.integration_link(cl[0])} now supports OAuth2 sign-in authentication`);
                                        break;
                                    case 'oauth2':
                                        desc.push(`${this.integration_link(cl[0])} added support for OAuth2 authorization`);
                                        break;
                                    case 'object':
                                        desc.push(
                                            `${this.integration_link(cl[0])} added ${this.join(cl.map((f) => this.object_name(f.object_type)))} ${cl.length > 1 ? 'objects' : 'object'
                                            }`
                                        );
                                        break;
                                    case 'pagination':
                                        objects.forEach((object) => {
                                            desc.push(
                                                `${this.integration_link(cl[0])} ${this.object_name(object)} added ${this.join(
                                                    cl.filter((c) => c.object_type === object).map((c) => c.pagination.replace('list_', ''))
                                                )} <b>pagination</b> support`
                                            );
                                        });
                                        break;
                                    // case 'integration':
                                    //     desc = `${this.integration_link(cl[0])}`;
                                    //     break;
                                    case 'webhook-created-native':
                                    case 'webhook-created-virtual':
                                    case 'webhook-updated-native':
                                    case 'webhook-updated-virtual':
                                    case 'webhook-deleted-native':
                                    case 'webhook-deleted-virtual':
                                        desc.push(
                                            `${this.integration_link(cl[0])} added '${cl_type.split('-')[1]}' ${cl_type.split('-')[2]} <b>webhooks</b> for ${this.join(cl.map((f) => this.object_name(f.object_type)))} ${cl.length > 1 ? 'objects' : 'object'
                                            }`
                                        );
                                        break;

                                    case 'method':
                                        objects.forEach((object) => {
                                            const methods = cl.filter((c) => c.object_type === object).map((f) => f.method)
                                            desc.push(
                                                `${this.integration_link(cl[0])} added '${this.join(methods)}' <b>method${methods.length > 1 ? 's' : ''}</b> for ${this.object_name(object)}`
                                            );
                                        });
                                        break;
                                    // objects.forEach((object) => {
                                    // desc.push(`${this.integration_link(cl[0])} ${this.object_name(object)} added support for '${cl_type.split('-')[1]}' <b>webhooks</b>`);
                                    // });
                                    // break;
                                }

                                if (desc?.length) {
                                    release.description += desc.map((d) => `<li>${d}</li>`).join('\n');
                                }
                            }
                        }

                        release.description += '</ul>';

                        return release;
                        // {
                        //     title: 'New readable field',
                        //     description: `${this.integration_link(c)} object ${this.object_name(c.object_type)} added new readable field ${c.field}`,
                        //     tags: [c.integration_name, this.object_name(c.object_type), 'Readable Field'],
                        // };
                    })
                )
                .sort((a, b) => b.released_at.localeCompare(a.released_at)) as IRelease[],
        };
    },
    methods: {
        unique(array: any[], field: string) {
            const set = new Set<string>();
            array.forEach((a) => set.add(a[field]));
            return [...set];
        },
        join(strings?: string[]) {
            if (!strings) {
                return '';
            }
            if (strings.length === 0) {
                return '';
            }
            if (strings.length === 1) {
                return strings[0];
            }
            return strings.slice(0, strings.length - 1).join(', ') + ' and ' + strings[strings.length - 1];
        },
        object_name(obj: TObjectType) {
            const name = obj.split('_')[1] || obj;
            const cat = obj.split('_')[0];

            return `<b><a target="_blank" href="https://docs.unified.to/${cat}/${name}/model">${name.substring(0, 1).toUpperCase() + name.substring(1)
                }</a></b>`;
        },
        integration_link(i: Pick<TChangelog, 'integration_name' | 'integration_type'>) {
            return `<a href="https://unified.to/integrations/${i.integration_type}">${i.integration_name}</a>`;
        },
        formatDate(time: string) {
            return time.replace(/\//g, '-');
            // const date = new Date(time);

            // // Get year, month, and day part from the date
            // const year = date.toLocaleString('default', { year: 'numeric' });
            // const month = date.toLocaleString('default', { month: '2-digit' });
            // const day = date.toLocaleString('default', { day: '2-digit' });

            // return `${year}/${month}/${day}`;
        },
    },
    // mounted() {
    // console.log(this._RELEASES.length);
    // RELEASES.forEach((r) => {
    //     this._RELEASES.push({
    //         title: r.title,
    //         description: Markdown.render(r.markdown || ''),
    //         released_at: r.released_at,
    //         tags: r.tags,
    //     });
    // });
    // INTEGRATIONS.forEach((i) => {
    //     if (!i.created_at || i.in_progress) {
    //         return;
    //     }
    //     this._RELEASES.push({
    //         title: i.name,
    //         description: Markdown.render(`New ${i.categories.map((c) => MAP_CATS[c]).join('/')} integration: [${i.name}](/integrations/${i.type})`),
    //         released_at: this.formatDate(i.created_at),
    //         tags: i.categories,
    //     });
    // });
    // console.log(this._RELEASES[0]);
    // this.RELEASES =
    // this._RELEASES.sort((a, b) => b.released_at.localeCompare(a.released_at));
    // console.log(this._RELEASES.length);
    // },
};
</script>

<style>
.timeline-desc a {
    text-decoration: underline;
}
</style>
